import React, { useRef, useEffect, useCallback } from 'react';
import { EditorView, keymap, placeholder as cmPlaceholder } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { defaultKeymap } from '@codemirror/commands';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags } from '@lezer/highlight';
import { linter, type Diagnostic } from '@codemirror/lint';
import { sql } from '@codemirror/lang-sql';
import {
  Button,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';

const WRITE_KEYWORDS = /\b(CREATE|MERGE|SET|DELETE|REMOVE|DROP|CALL|FOREACH)\b|LOAD\s+CSV/i;

const CYPHER_KEYWORDS = /\b(MATCH|OPTIONAL\s+MATCH|WHERE|RETURN|WITH|UNWIND|CREATE|MERGE|DELETE|DETACH\s+DELETE|SET|REMOVE|ORDER\s+BY|SKIP|LIMIT|UNION|CALL|YIELD|FOREACH|AS|AND|OR|NOT|IN|IS|NULL|TRUE|FALSE|EXISTS|CONTAINS|STARTS\s+WITH|ENDS\s+WITH|DISTINCT|CASE|WHEN|THEN|ELSE|END)\b/gi;

interface CypherEditorProps {
  value: string;
  onChange: (value: string) => void;
  onExecute: () => void;
  executing: boolean;
  error: string | null;
}

export const CypherEditor: React.FC<CypherEditorProps> = ({ value, onChange, onExecute, executing, error }) => {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  const onExecuteRef = useRef(onExecute);
  onChangeRef.current = onChange;
  onExecuteRef.current = onExecute;

  const cypherHighlight = HighlightStyle.define([
    { tag: tags.keyword, color: theme.colors.semantic.info.default, fontWeight: '600' },
    { tag: tags.string, color: '#a3be8c' },
    { tag: tags.number, color: '#d08770' },
    { tag: tags.comment, color: theme.colors.text.secondary, fontStyle: 'italic' },
  ]);

  const cypherLinter = useCallback(() => {
    return linter((view) => {
      const doc = view.state.doc.toString();
      const diagnostics: Diagnostic[] = [];
      const stripped = doc.replace(/'[^']*'/g, '').replace(/"[^"]*"/g, '');

      // Check for missing RETURN
      if (/\bMATCH\b/i.test(stripped) && !/\bRETURN\b/i.test(stripped)) {
        diagnostics.push({
          from: 0,
          to: doc.length,
          severity: 'warning',
          message: 'Query has MATCH but no RETURN clause',
        });
      }

      // Check for unclosed quotes
      const singleQuotes = (doc.match(/'/g) || []).length;
      const doubleQuotes = (doc.match(/"/g) || []).length;
      if (singleQuotes % 2 !== 0) {
        diagnostics.push({ from: 0, to: doc.length, severity: 'error', message: 'Unclosed single quote' });
      }
      if (doubleQuotes % 2 !== 0) {
        diagnostics.push({ from: 0, to: doc.length, severity: 'error', message: 'Unclosed double quote' });
      }

      // Check for unmatched parentheses
      let parenCount = 0;
      for (const ch of stripped) {
        if (ch === '(') parenCount++;
        if (ch === ')') parenCount--;
        if (parenCount < 0) break;
      }
      if (parenCount !== 0) {
        diagnostics.push({ from: 0, to: doc.length, severity: 'error', message: 'Unmatched parentheses' });
      }

      // Check for write operations
      if (WRITE_KEYWORDS.test(stripped)) {
        diagnostics.push({ from: 0, to: doc.length, severity: 'error', message: 'Write operations not allowed (read-only)' });
      }

      return diagnostics;
    });
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const executeKeymap = keymap.of([{
      key: 'Ctrl-Enter',
      mac: 'Cmd-Enter',
      run: () => {
        onExecuteRef.current();
        return true;
      },
    }]);

    const updateListener = EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        onChangeRef.current(update.state.doc.toString());
      }
    });

    const state = EditorState.create({
      doc: value,
      extensions: [
        sql(),
        syntaxHighlighting(cypherHighlight),
        cypherLinter(),
        executeKeymap,
        keymap.of(defaultKeymap),
        updateListener,
        cmPlaceholder('MATCH (n) RETURN n LIMIT 25'),
        EditorView.theme({
          '&': {
            fontSize: '0.85rem',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
          },
          '.cm-content': {
            padding: spacing.sm,
            minHeight: '120px',
          },
          '.cm-editor': {
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: '4px',
            background: theme.colors.background.elevated,
          },
          '.cm-focused': {
            outline: 'none',
          },
          '.cm-gutters': {
            display: 'none',
          },
        }),
        EditorView.lineWrapping,
      ],
    });

    const view = new EditorView({ state, parent: containerRef.current });
    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Initialize once

  // Sync external value changes into the editor
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const currentDoc = view.state.doc.toString();
    if (currentDoc !== value) {
      view.dispatch({
        changes: { from: 0, to: currentDoc.length, insert: value },
      });
    }
  }, [value]);

  // Client-side validation for the status bar
  const stripped = value.replace(/'[^']*'/g, '').replace(/"[^"]*"/g, '');
  const isWriteQuery = WRITE_KEYWORDS.test(stripped);
  const validationError = isWriteQuery ? 'Write operations not allowed.' : null;

  return (
    <div>
      <div ref={containerRef} style={{ color: theme.colors.text.primary }} />
      <Flex justify="between" align="center" style={{ marginTop: spacing.xs }}>
        <div style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>
          Ctrl+Enter to execute | Read-only queries only
        </div>
        <Flex gap="xs" align="center">
          {(validationError || error) && (
            <span style={{ fontSize: '0.8rem', color: theme.colors.semantic.error.default }}>
              {validationError || error}
            </span>
          )}
          <Button
            variant="solid"
            color="primary"
            size="sm"
            onClick={onExecute}
            disabled={executing || !value.trim() || !!validationError}
          >
            {executing ? 'Executing...' : 'Execute'}
          </Button>
        </Flex>
      </Flex>
    </div>
  );
};
